import {HttpClient, HttpErrorResponse, HttpEvent, HttpParams, HttpResponse} from '@angular/common/http';
import {filter, firstValueFrom, map, Observable} from 'rxjs';
import {Pagination} from '@models/pagination.model';
import {AdminUserLookupItem, AdminUserLookupRole} from '@shared/api/models/query/contract.model';
import {environment} from '../../../../../environments/environment';

/**
 * Helpers shared by the admin contract and organisation services.
 *
 * The global SampleInterceptor swallows 401/403/404/500: it toasts (403/404/500) and resolves the
 * call with the error message string instead of failing it. Every admin call routes through
 * `responseBody` so such a response becomes a rejection again, carrying the HTTP status read
 * back from that message, and the pages never dereference a string as if it were a payload.
 * `alreadyNotified` tells callers the interceptor already showed the toast (403/404/500 only).
 */
export class ContractRequestError extends HttpErrorResponse {
    readonly alreadyNotified: boolean;

    constructor(status: number) {
        super({status, statusText: 'Swallowed by interceptor'});
        this.alreadyNotified = [403, 404, 500].includes(status);
    }
}

/** True for errors whose toast the global interceptor already showed. */
export function isAlreadyNotified(error: unknown): boolean {
    return (error as ContractRequestError | undefined)?.alreadyNotified === true;
}

/**
 * RxJS operator over `observe: 'events'` calls. With the default `observe: 'body'` HttpClient drops
 * the interceptor's string (it only passes HttpResponse through), so the call would complete empty
 * and the status would be lost; with events the string arrives and is rethrown here.
 */
export function responseBody<T>() {
    return (source: Observable<HttpEvent<T>>): Observable<T> => source.pipe(
        filter((event) => typeof event === 'string' || event instanceof HttpResponse),
        map((event) => {
            if (typeof event === 'string') {
                const status = Number(/:\s(\d{3})\b/.exec(event)?.[1] ?? 0);
                throw new ContractRequestError(status);
            }
            return (event as HttpResponse<T>).body as T;
        }));
}

/** `size` and `start` (a row offset) for the paginated admin endpoints, plus `sort` and `order` when set. */
export function paginationParams(pagination: Pagination, params: HttpParams = new HttpParams()): HttpParams {
    let result = params
        .set('size', pagination.size)
        .set('start', pagination.start * pagination.size);
    if (pagination.sort) {
        result = result.set('sort', pagination.sort.prop).set('order', pagination.sort.order);
    }
    return result;
}

/**
 * Page size for the candidate-admin popups. `GET /api/v2/users/admins` caps `size` at 100 and
 * treats `start` as a row offset, so paging uses the real cap and stops on a page shorter than
 * it: a larger size would be cut to 100 server-side and end the loop after the first page.
 */
const CANDIDATE_ADMINS_PAGE_SIZE = 100;
/** Safety bound on the paging loop (100 x 100 = 10 000 users). */
const CANDIDATE_ADMINS_MAX_PAGES = 100;

/**
 * `GET /api/v2/users/admins?role=<role>` (general admin only on the backend): every enabled user
 * of that role, sorted by last name, first name, id. Items are exactly `id`, `firstName`,
 * `lastName`, `email`, `role`.
 */
export async function listAdminUsersByRole(http: HttpClient, role: AdminUserLookupRole): Promise<AdminUserLookupItem[]> {
    const candidates: AdminUserLookupItem[] = [];
    for (let page = 0; page < CANDIDATE_ADMINS_MAX_PAGES; page++) {
        const params = new HttpParams()
            .set('role', role)
            .set('size', CANDIDATE_ADMINS_PAGE_SIZE)
            .set('start', page * CANDIDATE_ADMINS_PAGE_SIZE);
        const rows = await firstValueFrom(http
            .get<{ data: AdminUserLookupItem[] }>(`${environment.baseApiUrl}v2/users/admins`, {params, observe: 'events'})
            .pipe(responseBody(), map((response) => response?.data ?? [])));
        candidates.push(...rows);
        if (rows.length < CANDIDATE_ADMINS_PAGE_SIZE) {
            break;
        }
    }
    return candidates;
}
